import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import 'react-calendar/dist/Calendar.css';
import CalendarTodayRoundedIcon from '@material-ui/icons/CalendarTodayOutlined';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import RadioGroup from '@material-ui/core/RadioGroup';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Button from '@material-ui/core/Button';
import { RadioButton } from 'components/buttons/RadioButtons';
import WidgetButton from 'components/buttons/WidgetButton';
import ExpansionPanel from 'components/ExpansionPanel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import ConditionalCustomDate from 'components/ConditionalCustomDate/ConditionalCustomDate';
import { dateMatchTypeOptions, dynamicDateOptions, customDateMatchTypeOptions } from 'components/buttons/ActionButtons/DateButton/content';

const useStyles = makeStyles(() => ({
  button: {
    marginRight: '20px'
  },
  popper: {
    zIndex: '1000'
  },
  icon: {
    fontSize: 'medium'
  }
}));

const DashboardDateButton = ({
  startDate,
  endDate,
  dateRangeTypeId,
  dateMatchType,
  customDateMatchType,
  setDateState
}) => {
  const classes = useStyles();
  const initialExpansion = 'customDate';
  const [startDateState, setStartDateStateDate] = useState(
    new Date(startDate )
  );
  const [endDateState, setEndDateStateDate] = useState(new Date(endDate));
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(initialExpansion);
  const [dateRangeIdValue, setDateRangeIdValue] = useState(`${dateRangeTypeId}`);
  const [dateMatchTypeValue, setDateMatchTypeValue] = useState(`${dateMatchType}`);
  const [customDateMatchTypeValue, setCustomDateMatchTypeValue] = useState(
    `${2}`
  );

  const handleExpansionClick = event => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (startDate) setStartDateStateDate(new Date(startDate));
  }, [startDate]);

  useEffect(() => {
    if (endDate) setEndDateStateDate(new Date(endDate));
  }, [endDate]);

  useEffect(() => {
    setExpanded(initialExpansion);
  }, [initialExpansion]);

  const handleExpandedChange = event => {
    if (event.currentTarget.id === expanded) {
      setExpanded('');
      return;
    }
    setExpanded(event.currentTarget.id);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const onCustomDateSubmit = () => {
    const newData = {
      startDate: moment(startDateState).format('YYYY-MM-DD'),
      endDate: moment(endDateState).format('YYYY-MM-DD'),
      dateRangeTypeId: 0,
      customDateRangeMatchType: Number(customDateMatchTypeValue)
    };
    setDateState(newData);
    setDateRangeIdValue('');
    handleClickAway();
  };

  const onDateRangeIdSubmit = () => {
    const newData = { dateRangeTypeId: Number(dateRangeIdValue)};
    setDateState(newData);
    handleClickAway();
  };

  const onDateMatchTypeSubmit = () => {
    if (!dateMatchTypeValue) return;

    const newData = { dateMatchType: Number(dateMatchTypeValue)};
    setDateState(newData);
    handleClickAway();
  };

  const handleSetStartDate = newDate => {
    setStartDateStateDate(newDate);
  };

  const handleSetEndDate = newDate => {
    setEndDateStateDate(newDate);
  };

  const handleDateRangeIdChange = event => {
    setDateRangeIdValue(event.target.value);
  };

  const handleDateMatchTypeChange = event => {
    setDateMatchTypeValue(event.target.value);
  };

  const handleCustomDateMatchTypeChange = event => {
    setCustomDateMatchTypeValue(event.currentTarget.value);
  };

  const handleClick = event => {
    if (event.target.value === dateRangeIdValue && event.target.value !== undefined) {
      setDateRangeIdValue(0);
    } else {
      setDateRangeIdValue(event.target.value);
    }
  };

  return (
    <>
      <WidgetButton onClick={handleExpansionClick}>
        <CalendarTodayRoundedIcon className={classes.icon} />
      </WidgetButton>
      <Popper
        open={!!anchorEl}
        placement="bottom-right"
        anchorEl={anchorEl}
        transition
        disablePortal
        className={classes.popper}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper>
            <ExpansionPanel
              title="Custom Date"
              id="customDate"
              expanded={expanded}
              handleChange={handleExpandedChange}
            >
              <Box p={2} maxWidth="360px">
                <Grid
                  container
                  direction="row"
                  justify="flex-start"
                  alignItems="flex-start"
                  spacing={1}
                >
                  {customDateMatchTypeOptions &&
                    customDateMatchTypeOptions.map(option => (
                      <Grid item xs={4} key={option.title}>
                        <Button
                          variant={
                            customDateMatchTypeValue === option.value ? 'contained' : 'outlined'
                          }
                          color="primary"
                          value={option.value}
                          onClick={handleCustomDateMatchTypeChange}
                        >
                          {option.title}
                        </Button>
                      </Grid>
                    ))}
                </Grid>
                <Box>
                  <ConditionalCustomDate
                    handleSetStartDate={handleSetStartDate}
                    startDateState={startDateState}
                    endDateState={endDateState}
                    handleSetEndDate={handleSetEndDate}
                    component={customDateMatchTypeValue}
                  />
                </Box>
              </Box>

              <Box px={1} pb={1}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={onCustomDateSubmit}
                >
                  Save
                </Button>
                <Button color="primary" className={classes.button} onClick={handleClickAway}>
                  Cancel
                </Button>
              </Box>
            </ExpansionPanel>
            <ExpansionPanel
              title="Dynamic Date"
              id="dynamicDate"
              expanded={expanded}
              handleChange={handleExpandedChange}
            >
              <Box p={2} maxWidth="360px">
                <h3>Show me data for:</h3>
                <RadioGroup
                  aria-label="quiz"
                  name="quiz"
                  value={dateRangeIdValue}
                  onChange={handleDateRangeIdChange}
                >
                  <Grid
                    container
                    direction="row"
                    justify="flex-start"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    {dynamicDateOptions &&
                      dynamicDateOptions.map(option => (
                        <Grid item xs={6} key={option.title}>
                          <FormControlLabel
                            value={option.value}
                            control={<Radio color="primary" onClick={handleClick} />}
                            label={option.title}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </RadioGroup>
              </Box>
              <Box px={1} pb={1}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={onDateRangeIdSubmit}
                >
                  Save
                </Button>
                <Button color="primary" className={classes.button} onClick={handleClickAway}>
                  Cancel
                </Button>
              </Box>
            </ExpansionPanel>
            <ExpansionPanel
              title="Date Match Type"
              id="dateMatchType"
              expanded={expanded}
              handleChange={handleExpandedChange}
            >
              <Box p={2} maxWidth="360px">
                <h3>Choose:</h3>
                <RadioGroup
                  aria-label="quiz"
                  name="quiz"
                  value={dateMatchTypeValue}
                  onChange={handleDateMatchTypeChange}
                >
                  <Grid
                    container
                    direction="row"
                    justify="flex-start"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    {dateMatchTypeOptions &&
                      dateMatchTypeOptions.map(option => (
                        <Grid item xs={6} key={option.title}>
                          <RadioButton title={option.title} value={option.value} />
                        </Grid>
                      ))}
                  </Grid>
                </RadioGroup>
              </Box>
              <Box px={1} pb={1}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={onDateMatchTypeSubmit}
                >
                  Save
                </Button>
                <Button color="primary" className={classes.button} onClick={handleClickAway}>
                  Cancel
                </Button>
              </Box>
            </ExpansionPanel>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

DashboardDateButton.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  dateRangeTypeId: PropTypes.number.isRequired,
  dateMatchType: PropTypes.number.isRequired,
  customDateMatchType: PropTypes.number.isRequired,
  startPeriod: PropTypes.string,
  endPeriod: PropTypes.string
};

DashboardDateButton.defaultProps = {
  startDate: null,
  endDate: null,
  startPeriod: null,
  endPeriod: null,
};

export default DashboardDateButton;
